/**
 * Copilot animation timing — THE control panel.
 *
 * Every duration / delay / lead the Copilot's streaming text, composing wait, count read-up, and chart
 * build use is defined HERE. Change a number in this file to retime that part of the experience
 * everywhere — nothing else hard-codes these values. `motion.ts` derives its variants / ECharts config /
 * stagger functions from these, and the UI components import them directly.
 *
 * Units: `*Ms` = milliseconds (ECharts + CSS), `*Sec` = seconds (framer-motion transitions).
 */
export const TIMING = {
  // ── Streamed assistant prose ────────────────────────────────────────────────
  wordFadeMs: 320, // each newly arrived word fades in over this

  // ── Composing wait (model thinking, before the answer lands) ────────────────
  shimmerSweepSec: 2.4, // cardinal-highlight sweep period on the "Composing…" / "Querying…" line

  // ── Sequential reveal: query → card → chart parts (each its own cinematic beat) ──
  // 1) The query panel (plain-language + EXPANDED SQL) leads, read before anything else. Counted charts
  //    use the count read-up below as their (equally long) lead.
  queryLeadMs: 2400,
  cardRevealSec: 0.75, // the empty chart card's slow fade/blur/rise in (its own step)

  // ── Instrument readout: the in-range record count rolls up, then holds ──────
  countReadUpSec: 1.9, // count tween duration
  countHoldMs: 600, // locked, legible hold before the card joins

  // ── Chart build (ECharts entrance) ──────────────────────────────────────────
  // The card materializes (cardRevealSec) and HOLDS, then the data draws — so frame beat > card reveal.
  frameBeatMs: 1000, // axes/grid hold (card already in) before any data draws
  seriesDrawMs: 1900, // one series' draw-in duration (slow, cinematic)
  seriesLeadMs: 700, // each line/area layer starts this far behind the previous
  markSweepMs: 1500, // bars / points / cells per-mark sweep span (frame-led)
  markStepCapMs: 80, // max per-mark offset so wide series don't crawl
  trendSweepMs: 900, // the trend / regression overlay's quicker sweep-through

  // ── Answer card entrances ───────────────────────────────────────────────────
  chartRevealSec: 0.22, // chart card opacity settle (fast, so the ECharts draw isn't masked)
  cardEntranceSec: 0.4, // non-chart answer (prose / clarify / table) entrance
  listStaggerSec: 0.08, // stagger between list children

} as const;

/**
 * Derived: how long the chart is held back on the COUNTED path so the count read-up is legible before
 * the chart joins. Kept here (next to its inputs) so the roll and the hold can't drift apart.
 */
export const COUNT_LEAD_MS = TIMING.countReadUpSec * 1000 + TIMING.countHoldMs;

/** The shared "settle" easing (a gentle decelerate) used by the card/chart entrances. */
export const SETTLE_EASE = [0.16, 1, 0.3, 1] as const;
